import React, { useEffect, useState } from "react"
import "./CountryPreferences.scss"
import {
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Button,
  Spinner,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import CountryPreferenceApi from "Api/CountryPreferenceApi"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import Country from "utils/Data/Country"
import { useCallback, useRef } from "react"

const CountryPreferences = () => {
  document.title = "Country Preferences"
  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Applications", link: "#" },
    { label: "Country Preferences", link: "#" },
  ]

  const [countries, setCountries] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [limit, setLimit] = useState(10)
  const [editingId, setEditingId] = useState(null)
  const [editingValue, setEditingValue] = useState("")
  const [modal, setModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const accessToken = localStorage.getItem("accessToken")

  const initialData = {
    name: "",
    code: "",
    preferenceNumber: "",
  }

  const [countryData, setCountryData] = useState(initialData)

  const fetchCountryPreferences = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }
    CountryPreferenceApi.getAllCountry(config)
      .then(response => {
        if (response.data && Array.isArray(response.data.data)) {
          setCountries(response.data.data)
          setTotalPages(response.data.pagination.totalPages)
        } else {
          console.log("API Response:", response.data)
          setCountries([])
          setTotalPages(0)
        }
        setLoading(false)
      })
      .catch(error => {
        console.error("Error fetching data:", error)
        setLoading(false)
        setCountries([])
      })
  }

  useEffect(() => {
    fetchCountryPreferences()
  }, [page, limit])

  const handlePreferenceClick = country => {
    setEditingId(country._id)
    setEditingValue(country.preferenceNumber)
  }

  const handleInputChange = e => {
    setEditingValue(e.target.value)
  }

  const handleInputBlurOrEnter = country => {
    const query = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    if (
      editingValue !== "" &&
      !isNaN(editingValue) &&
      Number(editingValue) > 0 &&
      Number(editingValue) !== country.preferenceNumber
    ) {
      CountryPreferenceApi.updateCountryPreference(
        {
          countryId: country._id,
          preferenceNumber: Number(editingValue),
        },
        query,
      )
        .then(() => {
          fetchCountryPreferences()
          setEditingId(null)
          setEditingValue("")
        })
        .catch(err => {
          // Optionally handle error
        })
    }
    setEditingId(null)
    setEditingValue("")
  }

  const [searchQuery, setSearchQuery] = useState("")

  const debounce = (func, delay) => {
    let timer
    return (...args) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const debouncedSearchRef = useRef()

  const searchCountries = useCallback(
    query => {
      if (query.trim() === "") {
        fetchCountryPreferences()
      } else {
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        CountryPreferenceApi.searchCountry({ query }, config)
          .then(response => {
            console.log("API Response:", response.data)
            if (response.data && Array.isArray(response.data.data)) {
              setCountries(response.data.data)
              if (
                response.data.pagination &&
                response.data.pagination.totalPages
              ) {
                setTotalPages(response.data.pagination.totalPages)
              } else {
                setTotalPages(1) // or 0 if you want to hide pagination
              }
            } else {
              console.log("API Response:", response.data)
              setCountries([])
              setTotalPages(0)
            }
          })
          .catch(error => {
            console.error("Error searching countries:", error)
            setCountries([])
            setTotalPages(0)
          })
      }
    },
    [fetchCountryPreferences, accessToken],
  )

  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce(searchCountries, 800)
  }

  const handleSearch = useCallback(
    e => {
      const query = e.target.value
      setSearchQuery(query)
      debouncedSearchRef.current(query)
    },
    [setSearchQuery],
  )

  const toggleModal = () => {
    setModal(!modal)
    if (!modal) {
      setCountryData(initialData)
    }
  }

  const handleFormInputChange = (field, value) => {
    setCountryData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddCountry = () => {
    if (!countryData.name || !countryData.code || !countryData.preferenceNumber) {
      return
    }

    setSaving(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    
    CountryPreferenceApi.addCountryPreference(countryData, config)
      .then(response => {
        console.log(response.data)
        setCountries([...countries, response.data.data])
        setCountryData(initialData)
        toggleModal()
      })
      .catch(error => {
        console.log(error)
      })
      .finally(() => {
        setSaving(false)
        setCountryData(initialData)
      })
  }

  return (
    <div className="country-preferences">
      <Row>
        <Col xs="12">
          <div className="list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Country Preferences"
            />

            <div className="d-flex align-items-center gap-3">
              <Input
                type="text"
                placeholder="Search Country"
                className="form-control"
                style={{ width: "300px" }}
                value={searchQuery}
                onChange={handleSearch}
              />
              <Button color="primary" onClick={toggleModal}>
                Add Country
              </Button>
            </div>
          </div>

          <div className="data-table table-responsive mt-4 mb-3">
            {loading ? (
              <DataTableLoader cols={7} />
            ) : (
              <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Country Name</th>
                    <th>Country Code</th>
                    <th>Preference Number</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(countries) &&
                    countries.map((country, index) => (
                      <tr key={country._id}>
                        <td>{country.name}</td>
                        <td>{country.code}</td>
                        <td
                          style={{
                            cursor: "pointer",
                            color: "#007bff",
                          }}
                        >
                          {editingId === country._id ? (
                            <input
                              type="number"
                              min={1}
                              value={editingValue}
                              autoFocus
                              onChange={handleInputChange}
                              onBlur={() => handleInputBlurOrEnter(country)}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  handleInputBlurOrEnter(country)
                                }
                              }}
                              style={{ width: 60 }}
                            />
                          ) : (
                            <span
                              onClick={() => handlePreferenceClick(country)}
                            >
                              {country.preferenceNumber}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container d-flex align-items-center justify-content-end">
              <Pagination>
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {[...Array(Math.min(3, totalPages))].map((_, index) => (
                  <PaginationItem key={index} active={index + 1 === page}>
                    <PaginationLink onClick={() => setPage(index + 1)}>
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                {totalPages > 3 && (
                  <PaginationItem disabled>
                    <PaginationLink>...</PaginationLink>
                  </PaginationItem>
                )}
                <PaginationItem disabled={page >= totalPages}>
                  <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </Col>
      </Row>

      {/* Add Country Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="md">
        <ModalHeader toggle={toggleModal}>
          Add New Country
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="countryName">Country Name</Label>
              <Input
                id="countryName"
                type="text"
                value={countryData.name}
                onChange={(e) => handleFormInputChange('name', e.target.value)}
                placeholder="Enter country name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="countryCode">Country Code</Label>
              <Input
                id="countryCode"
                type="text"
                value={countryData.code}
                onChange={(e) => handleFormInputChange('code', e.target.value)}
                placeholder="Enter country code (e.g., US, UK)"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="preferenceNumber">Preference Number</Label>
              <Input
                id="preferenceNumber"
                type="number"
                min="1"
                value={countryData.preferenceNumber}
                onChange={(e) => handleFormInputChange('preferenceNumber', e.target.value)}
                placeholder="Enter preference number"
                required
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal} disabled={saving}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleAddCountry} 
            disabled={saving || !countryData.name || !countryData.code || !countryData.preferenceNumber}
          >
            {saving ? <Spinner size="sm" /> : 'Add Country'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default CountryPreferences
